/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, FormControl, Alert } from "react-bootstrap";
import {
  getAuthenticatedWebSocketUrl,
  getUserId,
} from "../../utilities/authHelper";
import { FaCopy, FaPen, FaRedo } from "react-icons/fa";
import { domToReact, htmlToDOM } from "html-react-parser";
import LinkPreview from "../ProductDashboard/Emgpt/LinkPreview";
import SourceChips from "../ProductDashboard/Emgpt/SourceChips";
import AnalysisSteps from "./AnalysisSteps";
import { SuggestionsSkeleton } from "./CoTraderSkeletons";
import { useResponsiveTileTypography } from "./Utilities/useResponsiveTileTypography";
import HighchartFromText from "../ProductDashboard/Emgpt/HighchartFromText";
import { giveFeedback } from "../../utilities/EmgptUtilities";
import useChatVoice, { htmlToSpeechText } from "../../utilities/useChatVoice";
import { fxgptChatSuggestionsByLanguage } from "../Common/ChatSuggestions";
import ChatOverlayFrame from "../Common/ChatOverlayFrame";
import { useFxSuggestedQuestions } from "./Utilities/CoTraderUtils";
import { getStoredCoTraderLanguage } from "./Utilities/languages";
import "../Common/chatSurface.css";
import "./CoTrader.css";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import {
  getChatSuggestionsFromPayload,
  normalizeChatSuggestion,
  normalizeChatSuggestions,
} from "../../utilities/chatSuggestionUtilities";

const VOICE_IDLE_AUTO_SEND_MS = 1000;
const EMPTY_RESPONSE_RETRY_LIMIT = 1;
const EMPTY_RESPONSE_FALLBACK_TEXT =
  "No visible response was received. Please try again.";
// Opens an analysis block the moment a question is sent; the socket's own INFO
// steps stream in underneath it and the block is closed as usual (GlobalLink
// pre-trade behaviour).
const PROCESSING_PLACEHOLDER = "<CollapsibleDiv>Processing\n";

const CoTraderEmgptDiv = ({
  selectedSymbol,
  selectedTickerLabel,
  searchFlag,
  setSearchFlag,
  showSuggestions,
  setShowSuggestions,
  tabs,
  setTabs,
  useFxSuggestedQuestionsApi = false,
  onConversationStateChange,
  externalQuestionRequest,
  assistantLabel = "FX GPT",
  suggestionOverrides,
  // setTvTickers,
  // setActiveTvSymbol,
  // activeTvSymbol,
}) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const wsRef = useRef(null); // to hold the active WebSocket
  const isCancelledRef = useRef(false);
  const [feedbackMessageId, setFeedbackMessageId] = useState([]);
  const [feedbackTexts, setFeedbackTexts] = useState({});
  const [feedbackTypes, setFeedbackTypes] = useState({});
  const [summary, setSummary] = useState("");
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const { containerRef: chatShellRef, tier: chatTier } = useResponsiveTileTypography();
  const voiceAutoSendTimerRef = useRef(null);
  const sendMessageRef = useRef(null);
  const lastExternalQuestionIdRef = useRef(null);
  const userLanguage = getStoredCoTraderLanguage();
  const {
    clearSpeechError,
    isListening,
    isSpeechRecognitionSupported,
    isSpeechSynthesisSupported,
    isTtsEnabled,
    resetVoiceInput,
    speakStreamingText,
    speechError,
    stopListening,
    stopSpeaking,
    toggleListening,
    toggleTts,
  } = useChatVoice({
    userInput,
    setUserInput,
    userLanguage,
  });
  const lastAnswerForSpeech = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.type !== "answer") continue;
      return {
        text: message?.text || "",
        streamKey: message?.message_id || `answer-${i}`,
      };
    }
    return null;
  }, [messages]);

  const handleFeedback = (messageId, feedback, feedbackText) => {
    giveFeedback({
      msgId: messageId,
      feedback,
      feedbackText,
      setFeedbackMessageId,
    });
  };
  const handleFeedbackTextChange = (messageId, text) => {
    setFeedbackTexts((prev) => ({
      ...prev,
      [messageId]: text,
    }));
  };

  const clearPendingFeedback = (messageId) => {
    setFeedbackTexts((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
    setFeedbackTypes((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
  };

  const openFeedbackForm = (messageId, feedbackType) => {
    setFeedbackTypes((prev) => ({ ...prev, [messageId]: feedbackType }));
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: "" }));
  };

  const submitFeedback = (messageId) => {
    const feedback = feedbackTypes[messageId] || "Negative";
    const feedbackText = feedbackTexts[messageId]?.trim() || feedback;
    handleFeedback(messageId, feedback, feedbackText);
    clearPendingFeedback(messageId);
  };

  const getFeedbackPlaceholder = (messageId) =>
    feedbackTypes[messageId] === "Positive"
      ? "Tell us what worked well..."
      : "Tell us what went wrong...";

  const {
    suggestedQuestions,
    suggestedQuestionsError,
    suggestedQuestionsLoading,
    suggestedQuestionsMeta,
  } = useFxSuggestedQuestions({
    instrument: selectedSymbol,
    language: userLanguage,
    enabled: useFxSuggestedQuestionsApi,
  });

  const suggestionInstrumentLabel = useMemo(() => {
    if (selectedTickerLabel === null || selectedTickerLabel === undefined) {
      return selectedSymbol;
    }

    return `${selectedTickerLabel} (${selectedSymbol.replace(/^EQ-?/i, "")})`;
  }, [selectedSymbol, selectedTickerLabel]);

  // Chat Suggestions
  const chatSuggestions = useMemo(() => {
    if (Array.isArray(suggestionOverrides) && suggestionOverrides.length > 0) {
      return normalizeChatSuggestions(suggestionOverrides);
    }

    const useApiSuggestions =
      useFxSuggestedQuestionsApi &&
      !suggestedQuestionsError &&
      (suggestedQuestionsLoading || suggestedQuestionsMeta);
    const suggestions = useApiSuggestions
      ? suggestedQuestions
      : fxgptChatSuggestionsByLanguage[userLanguage] ||
        fxgptChatSuggestionsByLanguage["English"];

    return normalizeChatSuggestions(suggestions).map((suggestion) => ({
      ...suggestion,
      question: suggestion.question.replace("{symbol}", suggestionInstrumentLabel),
    }));
  }, [
    suggestionOverrides,
    suggestedQuestions,
    suggestedQuestionsError,
    suggestedQuestionsLoading,
    suggestedQuestionsMeta,
    suggestionInstrumentLabel,
    useFxSuggestedQuestionsApi,
    userLanguage,
  ]);
  const { isDark } = useTheme();
  // Dark mode takes its chat tokens from ThemeProvider/darkTheme.css.
  const chatSurfaceVars = useMemo(
    () => isDark ? undefined : ({
      "--ea-chat-page-bg": "#FFFFFF",
      "--ea-chat-surface": "#FFFFFF",
      "--ea-chat-surface-alt": "#F7FBFF",
      "--ea-chat-border": "#E9ECEF",
      "--ea-chat-text": "#0B1220",
      "--ea-chat-muted": "#5B6472",
      "--ea-chat-link": "#0D6EFD",
      "--ea-chat-accent": "#0D6EFD",
      "--ea-chat-accent-soft": "rgba(13, 110, 253, 0.12)",
      "--ea-chat-question-bg": "linear-gradient(135deg, #0D6EFD 0%, #175CD3 100%)",
      "--ea-chat-question-text": "#FFFFFF",
      "--ea-chat-answer-bg": "rgba(255, 255, 255, 0.94)",
      "--ea-chat-answer-text": "#0B1220",
      "--ea-chat-body-bg": "linear-gradient(180deg, #F7FBFF 0%, #FFFFFF 52%)",
      "--ea-chat-composer-bg": "linear-gradient(180deg, #FBFDFF 0%, #F5F8FC 100%)",
      "--ea-chat-input-bg": "#FFFFFF",
      "--ea-chat-chip-bg": "#F5F9FF",
      "--ea-chat-ghost-bg": "rgba(255, 255, 255, 0.88)",
      "--ea-chat-toast-bg": "#0B1220",
      "--ea-chat-shadow": "0 22px 60px rgba(16, 24, 40, 0.10)",
    }),
    [isDark]
  );



  // UseEffects

  useEffect(() => {
    const messagesContainer = messagesEndRef.current;
    if (messagesContainer) {
      messagesContainer.scrollIntoView({ behavior: "smooth" });
    }
  }, [isLoading]);

  useEffect(() => {
    if (!onConversationStateChange) {
      return;
    }

    onConversationStateChange({
      hasConversation: messages.length > 0,
      isLoading,
    });
  }, [isLoading, messages.length, onConversationStateChange]);

  useEffect(() => {
      // Mark current stream as cancelled
      isCancelledRef.current = true;
      clearSpeechError();
      stopListening();
      stopSpeaking();
      resetVoiceInput();
      if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsLoading(false);
    setMessages([]);
    setDynamicSuggestions([]);
    setSummary("");
    setSummaryVersion(0);

    setTimeout(() => {
      isCancelledRef.current = false;
    }, 2);
    // Reset cancellation for next session
  }, [clearSpeechError, resetVoiceInput, selectedSymbol, stopListening, stopSpeaking]);

  useEffect(() => {
    if (!isTtsEnabled || !lastAnswerForSpeech) return;
    const textForSpeech = htmlToSpeechText(lastAnswerForSpeech.text);
    speakStreamingText(textForSpeech, {
      isFinal: !isLoading,
      streamKey: lastAnswerForSpeech.streamKey,
    });
  }, [isLoading, isTtsEnabled, lastAnswerForSpeech, speakStreamingText]);

  useEffect(() => {
    return () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      stopListening();
      stopSpeaking();
    };
  }, [stopListening, stopSpeaking]);

  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  // FXGPT: upgraded streaming + reconnection like EMGPT (requestBody unchanged)
  const handleSendMessage = async ({
    userInput,
    evidenceRefs = [],
    silentRetry = false,
    preservedContext = null,
    retryCount = 0,
  }) => {
    try {
      clearSpeechError();
      stopListening();
      stopSpeaking();
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
      // =========================
      // Early exits & setup
      // =========================
      setDynamicSuggestions([]);
      const trimmedInput = (userInput || "").trim();
      if (!trimmedInput) return;
      const normalizedEvidenceRefs = Array.isArray(evidenceRefs)
        ? evidenceRefs
        : [];

      setIsLoading(true);
      resetVoiceInput();

      const newMessage = { type: "question", text: trimmedInput };

      // Append user's question
      if (!silentRetry) {
        setMessages((prevMessages) => [...prevMessages, newMessage]);
      }

      // Build short context (last 4 Q/A only)
      const lastQAContext =
        typeof preservedContext === "string"
          ? preservedContext
          : messages
              .filter((msg) => msg.type === "question" || msg.type === "answer")
              .slice(-4)
              .map(
                (msg) =>
                  `${msg.type === "question" ? "Question:" : "Answer:"} ${msg.text}`
              )
              .join(" ") || "";

      // Seed the answer with an already-open analysis block so the header and
      // its timer render on send instead of waiting for the first INFO frame.
      // The trailing newline makes server steps append as their own rows.
      // We only mutate THIS answer afterwards.
      if (!silentRetry) {
        setMessages((prev) => [
          ...prev,
          { type: "answer", text: PROCESSING_PLACEHOLDER },
        ]);
      } else {
        setMessages((prev) => {
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              updated[i] = { type: "answer", text: PROCESSING_PLACEHOLDER };
              break;
            }
          }
          return updated;
        });
      }

      const user_id = getUserId();
      const random_number = Math.floor(100000 + Math.random() * 900000);
      const chat_id = `FXGPT-${user_id}-${random_number}`;
      const user_language = getStoredCoTraderLanguage();

      // ⚠️ Do not change predefined params in requestBody
      const requestBody = {
        query: `question: ${newMessage.text}`,
        context: lastQAContext,
        document_id: [""],
        dashboard_name: "",
        countries: [""],
        company_ids: [selectedSymbol],
        reference_ids: "",
        evidence_refs: normalizedEvidenceRefs,
        timestamp: new Date().toISOString(),
        chat_id: chat_id,
        sender: user_id,
        sender_display_name: "USER",
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        search_flag: searchFlag,
        show_messages: false,
        origin: "FXGPT",
        show_suggestions: showSuggestions,
        summary: summary,
        summary_version: summaryVersion,
        user_language: user_language,
      };

      // =========================
      // Frame-batched rendering
      // =========================
      let rafId = null;
      let streamBuf = ""; // text accumulated for the CURRENT frame

      const flushAnswerOncePerFrame = () => {
        if (!streamBuf) return;
        const chunk = streamBuf;
        streamBuf = "";
        // Mutate ONLY the last answer message
        setMessages((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          // Find last "answer" from the end (robust if other messages are added)
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i]?.type === "answer") {
              const prevText = updated[i].text || "";
              updated[i] = { ...updated[i], text: prevText + chunk };
              break;
            }
          }
          return updated;
        });
      };

      const scheduleFrameFlush = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          flushAnswerOncePerFrame();
        });
      };

      const enqueueText = (s) => {
        if (!s) return;
        streamBuf += s;
        scheduleFrameFlush();
      };

      // =========================
      // 25ms WS burst coalescer
      // =========================
      let wsBurstTimer = null;
      let wsBurstBuf = "";

      const addToBurst = (s) => {
        if (!s) return;
        wsBurstBuf += s;
        if (wsBurstTimer) return;
        wsBurstTimer = setTimeout(() => {
          enqueueText(wsBurstBuf);
          wsBurstBuf = "";
          wsBurstTimer = null;
        }, 25);
      };

      // =========================
      // WS lifecycle & streaming with reconnection
      // =========================
      let reconnectEnabled = true;
      let hasFinished = false;
      let reconnectAttempts = 0;
      const MAX_RECONNECT_ATTEMPTS = 5;
      let lastType = null; // to auto-close/open chart/info wrappers
      let activeAssistantMessageId = null;
      let hasRenderableContent = false;
      // The seeded placeholder already opened one.
      let collapsibleOpen = true;

      const closeCollapsibleIfOpen = () => {
        if (!collapsibleOpen) return;
        collapsibleOpen = false;
        addToBurst("</CollapsibleDiv>");
      };

      // Sources stay as data on the message instead of being spliced into its
      // markup; RenderFromString decides where they land.
      const addSourceUrls = (message) => {
        const incoming = String(message)
          .split(" ")
          .filter((url) => url);
        if (!incoming.length) return;

        setMessages((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              const merged = [...(updated[i].sources || [])];
              incoming.forEach((url) => {
                if (!merged.includes(url)) merged.push(url);
              });
              updated[i] = { ...updated[i], sources: merged };
              break;
            }
          }
          return updated;
        });
      };

      const applyEmptyResponseFallback = () => {
        setMessages((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              // The seeded analysis block is not an answer on its own.
              const currentText = (updated[i]?.text || "")
                .replace(/<\/?collapsiblediv>/gi, "")
                .replace(/^Processing\s*/, "");
              if (currentText.trim()) {
                return prev;
              }
              updated[i] = {
                ...updated[i],
                text: EMPTY_RESPONSE_FALLBACK_TEXT,
              };
              return updated;
            }
          }
          return prev;
        });
      };

      const resetReplayState = () => {
        if (wsBurstTimer) {
          clearTimeout(wsBurstTimer);
          wsBurstTimer = null;
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        wsBurstBuf = "";
        streamBuf = "";
        lastType = null;
        // The server replays the answer from the start; reopen the block.
        collapsibleOpen = true;
        setMessages((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              updated[i] = {
                ...updated[i],
                text: PROCESSING_PLACEHOLDER,
                sources: [],
              };
              return updated;
            }
          }
          return prev;
        });
      };

      const stopAll = () => {
        reconnectEnabled = false;
        try {
          wsRef.current?.close();
        } catch {}
        closeCollapsibleIfOpen();
        if (wsBurstTimer) {
          clearTimeout(wsBurstTimer);
          wsBurstTimer = null;
        }
        if (wsBurstBuf) {
          enqueueText(wsBurstBuf);
          wsBurstBuf = "";
        }
        // ensure any pending text is painted
        flushAnswerOncePerFrame();
        setIsLoading(false);
        // if consumer uses a cancel ref, reset it
        if (typeof isCancelledRef?.current !== "undefined") {
          isCancelledRef.current = false;
        }
      };

      const scheduleReconnect = () => {
        if (!reconnectEnabled || hasFinished || isCancelledRef?.current) return;
        if (!activeAssistantMessageId) {
          console.warn("Reconnect skipped because no active assistant message_id is available yet.");
          stopAll();
          return;
        }
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.error("Max reconnect attempts reached.");
          stopAll();
          return;
        }
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
        reconnectAttempts += 1;
        setTimeout(() => {
          void connectWS(true).catch((error) => {
            console.error("WebSocket reconnect auth/setup error:", error);
            if (!isCancelledRef?.current) {
              scheduleReconnect();
            }
          });
        }, delay);
      };

      const connectWS = async (isReconnect = false) => {
        if (!reconnectEnabled || hasFinished || isCancelledRef?.current) return;

        const wsUrl = await getAuthenticatedWebSocketUrl(
          chat_id,
          user_id,
          isReconnect ? activeAssistantMessageId : undefined
        );
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (isReconnect) {
            resetReplayState();
            return;
          }
          ws.send(JSON.stringify(requestBody));
        };

        ws.onmessage = (event) => {
          if (isCancelledRef?.current) {
            stopAll();
            return;
          }

          try {
            const data = JSON.parse(event.data);
            activeAssistantMessageId =
              data?.message_id ||
              (data.type === "MESSAGE_ID" && data.message != null
                ? String(data.message)
                : activeAssistantMessageId);

            if (data.message) {
              if (data.type === "YAHOO_FINANCE_TICKERS") {
                if (
                  data.message !== "False" &&
                  !tabs.find((tab) => tab.tvSymbol === data.message)
                ) {
                  setTabs((tabs) => [
                    ...tabs,
                    {
                      id: data.message,
                      label: data.message,
                      tvSymbol: data.message,
                    },
                  ]);
                  // setTvTickers((tickers) => [data.message, ...tickers]);
                }

                return;
              }

              if (data.type === "SUMMARY_UPDATE") {
                const summaryPayload =
                  typeof data.message === "string"
                    ? JSON.parse(data.message)
                    : data.message;
                setSummary(summaryPayload?.summary || "");
                setSummaryVersion(summaryPayload?.version || 0);
                return;
              }

              // Types that render nothing inline must not break the open
              // collapsible: "urls" is kept as data and shown under the last
              // analysis-steps collapsible.
              const isSilentType =
                data.type === "SUGGESTIONS" ||
                data.type === "MESSAGE_ID" ||
                data.type === "urls";

              if (lastType === "CHART" && data.type !== "CHART") {
                addToBurst("</chartrender>");
              }
              if (collapsibleOpen && data.type !== "INFO" && !isSilentType) {
                addToBurst("</CollapsibleDiv>");
                collapsibleOpen = false;
              }
              if (data.type === "INFO" && !collapsibleOpen) {
                addToBurst("<CollapsibleDiv>");
                collapsibleOpen = true;
              }
              if (data.type === "CHART" && lastType !== "CHART") {
                addToBurst("<chartrender>");
              }

              if (data.type === "urls") {
                hasRenderableContent = true;
                addSourceUrls(data.message);
              } else if (data.type === "SUGGESTIONS") {
                setDynamicSuggestions(getChatSuggestionsFromPayload(data.message));
              } else if (data.type !== "MESSAGE_ID") {
                if (
                  typeof data.message === "string" &&
                  data.message.trim().length > 0
                ) {
                  hasRenderableContent = true;
                }
                // Each INFO frame is one analysis step; the newline is what
                // the renderer splits rows on, so guarantee one per step.
                addToBurst(
                  data.type === "INFO" &&
                    typeof data.message === "string" &&
                    !/\n\s*$/.test(data.message)
                    ? `${data.message}\n`
                    : data.message
                );
              }

              lastType = data.type || null;
            }

            // Store message_id on the last answer (no re-render storm)
            if (data.type === "MESSAGE_ID") {
              setMessages((prev) => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i]?.type === "answer") {
                    updated[i] = { ...updated[i], message_id: data.message };
                    break;
                  }
                }
                return updated;
              });
            }

            if (data.type === "FINISH") {
              hasFinished = true;
              reconnectEnabled = false;
              activeAssistantMessageId = null;
              closeCollapsibleIfOpen();
              // flush any pending burst and frame immediately
              if (wsBurstTimer) {
                clearTimeout(wsBurstTimer);
                wsBurstTimer = null;
                enqueueText(wsBurstBuf);
                wsBurstBuf = "";
              }
              flushAnswerOncePerFrame();
              if (!hasRenderableContent) {
                if (retryCount < EMPTY_RESPONSE_RETRY_LIMIT) {
                  console.warn(
                    "CoTrader chat received an empty assistant response; retrying once."
                  );
                  try {
                    ws.close();
                  } catch {}
                  window.setTimeout(() => {
                    sendMessageRef.current?.({
                      userInput: trimmedInput,
                      evidenceRefs: normalizedEvidenceRefs,
                      silentRetry: true,
                      preservedContext: lastQAContext,
                      retryCount: retryCount + 1,
                    });
                  }, 0);
                  return;
                }

                applyEmptyResponseFallback();
              }
              try {
                ws.close();
              } catch {}
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };

        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
        };

        ws.onclose = () => {
          // Reconnect ONLY if not finished AND not cancelled AND still enabled
          if (!hasFinished && !isCancelledRef?.current && reconnectEnabled) {
            scheduleReconnect();
          } else {
            // Clean flags if consumer uses cancel
            if (typeof isCancelledRef?.current !== "undefined") {
              isCancelledRef.current = false;
            }
          }
        };
      };

      // Initial connect
      await connectWS(false);
    } catch (error) {
      console.error("handleSendMessage error:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    sendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  useEffect(() => {
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }

    if (!isListening || isLoading) return;
    const trimmedVoiceInput = (userInput || "").trim();
    if (!trimmedVoiceInput) return;

    voiceAutoSendTimerRef.current = setTimeout(() => {
      stopListening();
      sendMessageRef.current?.({ userInput: trimmedVoiceInput });
      voiceAutoSendTimerRef.current = null;
    }, VOICE_IDLE_AUTO_SEND_MS);

    return () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
    };
  }, [isListening, isLoading, stopListening, userInput]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage({ userInput });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const normalizedSuggestion = normalizeChatSuggestion(suggestion);
    if (!normalizedSuggestion) return;

    setUserInput(normalizedSuggestion.question);
    handleSendMessage({
      userInput: normalizedSuggestion.question,
      evidenceRefs: normalizedSuggestion.evidence_refs,
    });
  };

  useEffect(() => {
    if (
      !externalQuestionRequest?.id ||
      !externalQuestionRequest?.text ||
      lastExternalQuestionIdRef.current === externalQuestionRequest.id
    ) {
      return;
    }

    lastExternalQuestionIdRef.current = externalQuestionRequest.id;
    handleSuggestionClick(externalQuestionRequest.text);
  }, [externalQuestionRequest]);
  // Handle clicks outside the menu to close it

  const copyToClipboard = (htmlString) => {
    // Parse the HTML string to extract plain text
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const plainText = doc.body.textContent || "";

    // Copy plain text to clipboard
    navigator.clipboard.writeText(plainText).then(() => {
      alert("Copied to clipboard!");
    });
  };
  const regenerateAnswer = (index, realTime) => {
    setSearchFlag(realTime);
    const questionToRegenerate = messages[index - 1]?.text || "";
    handleSuggestionClick(questionToRegenerate);
  };

  const handleActionIconKeyDown = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  const ClearChat = () => {
    clearSpeechError();
    stopListening();
    stopSpeaking();
    resetVoiceInput();
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }
    setMessages([]);
    setFeedbackTexts({});
    setFeedbackTypes({});
    setTabs([]);
    setSummary("");
    setSummaryVersion(0);
  };

  return (
    <ChatOverlayFrame
      isOpen={isOverlayOpen}
      onClose={() => setIsOverlayOpen(false)}
    >
      <div
        ref={chatShellRef}
        data-tier={chatTier}
        className="w-100 h-100 d-flex flex-column ea-chat-surface ea-chat-shell ct-chat"
        style={chatSurfaceVars}
      >
        <div className="ea-chat-header d-flex align-items-center justify-content-between gap-3 flex-wrap text-dark-gray">
          {/* Logo and Heading */}
          <div className="d-flex align-items-center gap-3">
            <div className="ea-chat-title-group">
                <span className="ea-chat-title">
                  Ask EMAlpha
                  <span className="ct-chat-live">{assistantLabel}</span>
                </span>
                <p className="ea-chat-subtitle mb-0">
                  Tracking {selectedTickerLabel || selectedSymbol} with live market context.
                </p>
            </div>
          </div>

          {/* Clear Chat and Burger Menu */}
          <div className="ea-chat-header-actions">
            <Button
              type="button"
              onClick={() => setIsOverlayOpen((prev) => !prev)}
              className="btn btn-sm ea-chat-ghost-btn"
              title={isOverlayOpen ? "Exit full screen" : "Open full screen"}
              aria-pressed={isOverlayOpen}
            >
              <i
                className={`fa-solid ${
                  isOverlayOpen ? "fa-compress" : "fa-expand"
                } me-2`}
              />
              {isOverlayOpen ? "Exit" : "Expand"}
            </Button>
            {messages.length > 0 && (
              <Button
                onClick={ClearChat}
                className="btn btn-sm ea-chat-clear-btn"
                disabled={isLoading}
              >
                Clear Chat
              </Button>
            )}

            {/* Custom Burger Menu */}
          </div>
        </div>
        {/* ---- NEW: TradingView Tabs ---- */}
        {/* {tabs?.length > 0 && (
          <div className="mb-2 d-flex flex-row align-items-center">
            <div className="h7"> Tickers in chat:</div>
            <Nav variant="pills">
              {tabs.map((t) => {
                const isActive = activeTvSymbol === t.tvSymbol;
                return (
                  <Nav.Item key={t.id} className="me-2">
                    <Nav.Link
                      active={isActive}
                      onClick={() =>
                        setActiveTvSymbol(isActive ? null : t.tvSymbol)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {t.label}
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
            </Nav>
          </div>
        )}
        {activeTvSymbol && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              zIndex: 1050,
              background: "rgba(0,0,0,0.35)",
            }}
            onClick={() => setActiveTvSymbol(null)} // click backdrop closes
          >
            <div
              className="bg-white rounded shadow"
              style={{
                width: "96%",
                height: "86%",
                border: "1px solid var(--bs-border-color)",
              }}
              onClick={(e) => e.stopPropagation()} // prevent backdrop close on inner click
            >
              <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                <div className="fw-semibold">{activeTvSymbol}</div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setActiveTvSymbol(null)}
                >
                  Close
                </Button>
              </div>
              <div style={{ width: "100%", height: "calc(100% - 48px)" }}>
                <TradingViewChart symbol={activeTvSymbol} />
              </div>
            </div>
          </div>
        )} */}

        <div
          className="overflow-y-auto ea-chat-scroll"
          style={{ flex: 1, overflowX: "hidden" }}
        >
          {messages.length === 0 ? (
            <div className="bg-white flex-grow-1 ea-chat-empty">
              {chatSuggestions.length > 0 ? (
                <div className="ct-suggestions" id="suggestion_section">
                  {chatSuggestions.map((suggestion, index) => (
                    <button
                      type="button"
                      className="ct-suggestion"
                      key={`${suggestion.question}-${index}`}
                      title={suggestion.question}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <span>{suggestion.question}</span>
                    </button>
                  ))}
                </div>
              ) : suggestedQuestionsLoading ? (
                <SuggestionsSkeleton />
              ) : null}
            </div>
          ) : (
            <div className="bg-white flex-grow-1 overflow-auto">
              {messages.map((msg, index) => (
                <Alert
                  key={index}
                  variant={
                    msg.type === "question"
                      ? "primary"
                      : msg.type === "info"
                      ? "light"
                      : "secondary"
                  }
                  className={`text-${
                    msg.type === "question"
                      ? "right"
                      : msg.type === "info"
                      ? "center"
                      : "left"
                  } my-2 ea-chat-bubble ${
                    msg.type === "question"
                      ? "ea-chat-bubble--question"
                      : msg.type === "info"
                      ? "ea-chat-bubble--info"
                      : "ea-chat-bubble--answer"
                  }`}
                  style={{
                    maxWidth: msg.type === "question" ? "70%" : "100%",
                    marginLeft: msg.type === "question" ? "auto" : "0",
                    marginRight: msg.type === "question" ? "0" : "auto",
                  }}
                >
                  <div className="ea-chat-rich">
                    <RenderFromString
                      htmlString={msg.text}
                      sources={msg.sources}
                      isStreaming={
                        isLoading &&
                        msg.type === "answer" &&
                        index === messages.length - 1
                      }
                    />
                  </div>
                  {msg.type === "answer" && !isLoading && (
                    <>
                      <div className="ea-chat-actions">
                        <div className="ea-chat-action-group">
                          {msg.message_id &&
                            !feedbackMessageId.includes(msg.message_id) && (
                              <>
                                <div className="ea-chat-action-group">
                                  <Button
                                    className="ea-chat-icon-btn"
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() =>
                                      openFeedbackForm(msg.message_id, "Positive")
                                    }
                                  >
                                    <i className="fa-solid fa-thumbs-up" />
                                  </Button>
                                  <Button
                                    className="ea-chat-icon-btn"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      openFeedbackForm(msg.message_id, "Negative")
                                    }
                                  >
                                    <i className="fa-solid fa-thumbs-down" />
                                  </Button>
                                </div>
                              </>
                            )}
                        </div>

                        <div className="ea-chat-action-group ms-auto">
                          {/* Copy Button */}
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => copyToClipboard(msg.text)}
                            onKeyDown={(event) =>
                              handleActionIconKeyDown(event, () =>
                                copyToClipboard(msg.text)
                              )
                            }
                            title="Copy Message"
                          >
                            <FaCopy aria-hidden="true" />
                          </span>
                          {/* edit message */}
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            title="Edit Message"
                            onClick={() => {
                              setUserInput(messages[index - 1].text);
                              document.getElementById("userInput").focus();
                            }}
                            onKeyDown={(event) =>
                              handleActionIconKeyDown(event, () => {
                                setUserInput(messages[index - 1].text);
                                document.getElementById("userInput").focus();
                              })
                            }
                          >
                            <FaPen aria-hidden="true" />
                          </span>
                          {/* Regenerate Answer Button */}
                          <span
                            className="ea-chat-action-link"
                            role="button"
                            tabIndex={0}
                            title="Regenerate Answer"
                            onClick={() => regenerateAnswer(index, searchFlag)}
                            onKeyDown={(event) =>
                              handleActionIconKeyDown(event, () =>
                                regenerateAnswer(index, searchFlag)
                              )
                            }
                          >
                            <FaRedo aria-hidden="true" />
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                  {feedbackTexts[msg.message_id] !== undefined && (
                    <div className="mt-2 w-100">
                      <FormControl
                        className="ea-chat-feedback-textarea"
                        as="textarea"
                        rows={2}
                        placeholder={getFeedbackPlaceholder(msg.message_id)}
                        value={feedbackTexts[msg.message_id]}
                        onChange={(e) =>
                          handleFeedbackTextChange(
                            msg.message_id,
                            e.target.value
                          )
                        }
                      />
                      <Button
                        size="sm"
                        className="mt-1 ea-chat-feedback-submit"
                        onClick={() => {
                          submitFeedback(msg.message_id);
                        }}
                      >
                        Submit Feedback
                      </Button>
                    </div>
                  )}
                </Alert>
              ))}

              {dynamicSuggestions.length > 0 && !isLoading && (
                <div
                  className="ct-suggestions ct-suggestions--compact"
                  id="suggestion_section"
                >
                  {dynamicSuggestions.map((suggestion, index) => (
                    <button
                      type="button"
                      className="ct-suggestion"
                      key={`${suggestion.question}-${index}`}
                      title={suggestion.question}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <span>{suggestion.question}</span>
                    </button>
                  ))}
                </div>
              )}
              {/* Progress lives in the analysis-steps header, which shows the
                  live step and an elapsed timer while the answer streams. */}
              <div ref={messagesEndRef}></div>
            </div>
          )}
        </div>
        <div className="w-100 ea-composer is-docked">
          {speechError && <div className="ct-chat-hint is-error">{speechError}</div>}
          {isListening && !isLoading && (
            <div className="ct-chat-hint">
              Listening... auto-send after {VOICE_IDLE_AUTO_SEND_MS / 1000}s of silence.
            </div>
          )}
          <div className="ea-composer__field">
            <div className="ea-composer__input-wrap">
              <input
                type="text"
                placeholder={`Ask about ${selectedTickerLabel || selectedSymbol || "the market"}`}
                aria-label="Type your question"
                id="userInput"
                className="form-control ea-composer__input"
                value={userInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>
            {isSpeechRecognitionSupported && (
              <button
                type="button"
                className={`ea-composer__icon ${isListening ? "is-active" : ""}`}
                onClick={toggleListening}
                title={isListening ? "Stop voice input" : "Voice input"}
                aria-label={isListening ? "Stop voice input" : "Start voice input"}
                aria-pressed={isListening}
                disabled={isLoading}
              >
                <i className="fa-solid fa-microphone" aria-hidden="true" />
              </button>
            )}
            {isSpeechSynthesisSupported && (
              <button
                type="button"
                className="ea-composer__icon"
                onClick={toggleTts}
                title={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
                aria-label={isTtsEnabled ? "Disable voice output" : "Enable voice output"}
                aria-pressed={isTtsEnabled}
                style={isTtsEnabled ? { color: "#067647" } : undefined}
              >
                <i
                  className={`fa-solid ${isTtsEnabled ? "fa-volume-high" : "fa-volume-xmark"}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {isLoading ? (
              <button
                type="button"
                className="ea-composer__send is-stop"
                title="Stop generating"
                aria-label="Stop generating"
                onClick={() => {
                  stopListening();
                  stopSpeaking();
                  if (wsRef.current) {
                    wsRef.current.close();
                    wsRef.current = null;
                  }
                  setIsLoading(false);
                }}
              >
                <span className="ea-composer__stop-glyph" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="ea-composer__send"
                onClick={() => handleSendMessage({ userInput })}
                disabled={!(userInput ?? "").trim()}
                title="Send"
                aria-label="Send message"
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </ChatOverlayFrame>
  );
};

export default CoTraderEmgptDiv;

// Last collapsible in document order, however deeply it is nested.
const findLastCollapsible = (nodes) => {
  let last = null;
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (node?.name === "collapsiblediv") last = node;
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return last;
};

// INFO messages stream in back to back, so one collapsible holds every step as
// a flat run of nodes. Line breaks are the only separator the backend gives
// us; split on them so each step renders as its own row.
const splitIntoSteps = (children) => {
  const steps = [];
  let current = [];

  const endStep = () => {
    if (current.length) steps.push(current);
    current = [];
  };

  (children || []).forEach((node) => {
    if (node?.type === "text") {
      const pieces = String(node.data || "").split(/\r?\n/);
      pieces.forEach((piece, index) => {
        if (index > 0) endStep();
        // Plain strings render as-is; only real nodes need domToReact.
        if (piece.trim()) current.push(piece);
      });
      return;
    }
    if (node?.name === "br") {
      endStep();
      return;
    }
    current.push(node);
  });
  endStep();

  return steps;
};

// Mirrors GlobalLink pre-trade's renderer. `isStreaming` gates the live
// header, so a stopped stream never leaves a block spinning.
export const RenderFromString = ({ htmlString, sources, isStreaming }) => {
  if (typeof htmlString !== "string") {
    console.error(
      "RenderFromString expects a string but received:",
      htmlString
    );
    return null;
  }

  const sourceUrls = Array.isArray(sources) ? sources : [];
  const dom = htmlToDOM(htmlString);
  // Supporting sources hang off the bottom of the last analysis-steps
  // collapsible; with no collapsible to hold them they follow the answer.
  const lastCollapsible = sourceUrls.length ? findLastCollapsible(dom) : null;
  // An unclosed trailing <CollapsibleDiv> means steps are still streaming, so
  // the header shows the live step instead of the finished summary.
  const lowered = htmlString.toLowerCase();
  const openCollapsible =
    isStreaming &&
    lowered.lastIndexOf("<collapsiblediv>") > lowered.lastIndexOf("</collapsiblediv>")
      ? findLastCollapsible(dom)
      : null;

  const parseOptions = {
    replace: (domNode) => {
      if (domNode.name === "linkpreview") {
        const url = domNode.attribs?.url || "#";
        return <LinkPreview url={url} />;
      }
      if (domNode.name === "collapsiblediv") {
        // Pass the options down so link previews nested inside the analysis
        // steps are still replaced with real components.
        const steps = splitIntoSteps(domNode.children).map((step, index) => (
          <React.Fragment key={index}>
            {step.map((item, itemIndex) =>
              typeof item === "string" ? (
                item
              ) : (
                <React.Fragment key={itemIndex}>
                  {domToReact([item], parseOptions)}
                </React.Fragment>
              )
            )}
          </React.Fragment>
        ));

        return (
          <AnalysisSteps
            steps={steps}
            active={domNode === openCollapsible}
            footer={
              domNode === lastCollapsible ? (
                <SourceChips urls={sourceUrls} />
              ) : null
            }
          />
        );
      }
      if (domNode.name === "chartrender") {
        return (
          <HighchartFromText chartText={domNode.childNodes[0]?.data || ""} />
        );
      }
    },
  };

  return (
    <>
      {domToReact(dom, parseOptions)}
      {sourceUrls.length && !lastCollapsible ? (
        <SourceChips urls={sourceUrls} />
      ) : null}
    </>
  );
};
